import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useColumnFilters, HeaderFilter } from "@/components/ColumnFilter";
import { TaskReportPreview } from "@/components/TaskReportPreview";
import { useAuth } from "@/context/AuthContext";
import { CalendarClock, Plus, Sparkles, CheckCircle2, ArrowRight, Trash2, X, LayoutGrid, List, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_TONE = {
  upcoming: { pill: "border-[var(--line)] text-[var(--ink-2)] bg-[var(--bg)]", label: "Upcoming", dot: "bg-[var(--ink-2)]" },
  due: { pill: "border-[var(--warn)] text-[var(--warn)] bg-[var(--gold-soft)]", label: "Due", dot: "bg-[var(--warn)]" },
  overdue: { pill: "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]", label: "Overdue", dot: "bg-[var(--err)]" },
  filed: { pill: "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]", label: "Filed", dot: "bg-[var(--ok)]" },
};

export const STAGES = ["Data Gathering", "In Preparation", "Awaiting Approval", "Submitted"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const monthLabel = (d) => { const [y, m] = (d.due_date || "").slice(0, 7).split("-"); return y && m ? `${MONTHS_LONG[parseInt(m, 10) - 1]} ${y}` : ""; };
const COLS = {
  month: { value: monthLabel, sort: (d) => (d.due_date || "").slice(0, 7) },
  due: { date: true, value: (d) => (d.due_date || "").slice(0, 10), sort: (d) => (d.due_date || "").slice(0, 10) },
  deadline: { value: (d) => d.label },
  client: { value: (d) => d.client_name },
  period: { value: (d) => d.period },
  stage: { value: (d) => d.stage, sort: (d) => (d.stage ? STAGES.indexOf(d.stage) : 99) },
  status: { value: (d) => STATUS_TONE[d.status]?.label || d.status, sort: (d) => ["overdue", "due", "upcoming", "filed"].indexOf(d.status) },
};

export default function Compliance() {
  const [view, setView] = useState("timeline"); // 'timeline' | 'grid'
  const [gridCursor, setGridCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() }; // 0-indexed month
  });
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [msg, setMsg] = useState("");

  const [loadErr, setLoadErr] = useState("");
  async function load() {
    setLoadErr("");
    try {
      const [i, t] = await Promise.all([api.get("/compliance"), api.get("/compliance/deadline-types")]);
      setItems(i.data); setTypes(t.data);
    } catch (e) {
      setLoadErr(e.response?.status === 401 ? "Your session has expired — please sign in again." : "Could not load compliance deadlines. Please retry.");
    }
    api.get("/clients").then((c) => setClients(c.data)).catch(() => {});
  }
  useEffect(() => { load(); }, []);

  const ctl = useColumnFilters(items, COLS, { key: "due", dir: "asc" });
  const { user } = useAuth();
  const visible = ctl.view;

  const counts = useMemo(() => {
    const c = { total: visible.length, upcoming: 0, due: 0, overdue: 0, filed: 0 };
    visible.forEach((d) => { c[d.status] = (c[d.status] || 0) + 1; });
    return c;
  }, [visible]);

  async function createTask(id) {
    setMsg("");
    try {
      const r = await api.post(`/compliance/${id}/create-task`);
      setMsg(`Task created (#${r.data.task_id.slice(-6)}).`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to create task");
    }
  }

  async function setStage(id, stage) {
    setItems((xs) => xs.map((d) => (d.id === id ? { ...d, stage } : d)));
    try { await api.put(`/compliance/${id}`, { stage }); }
    catch (e) { setMsg(e?.response?.data?.detail || "Could not update stage"); load(); }
  }

  async function markFiled(id) {
    setMsg("");
    await api.put(`/compliance/${id}`, { status: "filed" });
    load();
  }

  async function del(id) {
    if (!window.confirm("Delete this compliance deadline?")) return;
    try {
      await api.delete(`/compliance/${id}`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div className="p-8 md:p-12">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><CalendarClock size={12} strokeWidth={1.5} /> Statutory Deadlines · {counts.total}</div>
          <h1 className="font-display text-4xl">Compliance.</h1>
          <div className="text-sm text-[var(--ink-2)] mt-1">Track statutory filings — Companies House, HMRC, Pension AE. Convert any deadline into a task with one click.</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="view-timeline" onClick={() => setView("timeline")} className={`btn-ghost p-2 ${view === "timeline" ? "border-[var(--ink)]" : ""}`} title="Timeline"><List size={16} strokeWidth={1.5} /></button>
          <button data-testid="view-grid" onClick={() => setView("grid")} className={`btn-ghost p-2 ${view === "grid" ? "border-[var(--ink)]" : ""}`} title="Month grid"><LayoutGrid size={16} strokeWidth={1.5} /></button>
          <button data-testid="suggest-btn" onClick={() => setShowSuggest(true)} className="btn-ghost flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.5} /> Auto-suggest
          </button>
          {user?.role === "admin" && <TaskReportPreview title="Daily Compliance Report" base="/compliance" testid="compliance-report" note="Data up to last midnight (UK time)." />}
          <button data-testid="add-deadline-btn" onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2">
            <Plus size={14} strokeWidth={1.5} /> Add deadline
          </button>
        </div>
      </div>

      {/* Compact KPI strip */}
      <div className="flex flex-wrap gap-3 mb-5" data-testid="compliance-kpis">
        <Kpi label="Upcoming" value={counts.upcoming} tone="muted" testid="kpi-upcoming" />
        <Kpi label="Due" value={counts.due} tone="warn" testid="kpi-due" />
        <Kpi label="Overdue" value={counts.overdue} tone="err" testid="kpi-overdue" />
        <Kpi label="Filed" value={counts.filed} tone="ok" testid="kpi-filed" />
      </div>

      {ctl.anyActive && (
        <div className="flex items-center gap-2 mb-4 text-xs" data-testid="compliance-filters">
          <span className="text-[var(--ink-2)]">Showing {visible.length} of {items.length} deadlines</span>
          <button data-testid="clear-filters" onClick={ctl.clearAll} className="underline text-[var(--ink-2)] hover:text-[var(--ink)]">Clear all filters</button>
        </div>
      )}

      {loadErr && (
        <div className="text-sm mb-3 p-2 rounded-sm border border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)] flex items-center justify-between" data-testid="compliance-load-error">
          <span>{loadErr}</span>
          <button data-testid="compliance-retry" onClick={load} className="text-xs underline">Retry</button>
        </div>
      )}
      {msg && <div className="text-sm text-[var(--ink-2)] mb-3" data-testid="compliance-msg">{msg}</div>}

      {/* Views */}
      {items.length === 0 ? (
        <div className="card-flat p-16 text-center" data-testid="compliance-empty">
          <CalendarClock size={40} strokeWidth={1} className="mx-auto text-[var(--ink-2)] mb-3" />
          <div className="text-sm text-[var(--ink-2)]">No compliance deadlines yet. Use <span className="font-medium">Auto-suggest</span> to generate them from client data, or add manually.</div>
        </div>
      ) : view === "grid" ? (
        <MonthGrid
          cursor={gridCursor}
          setCursor={setGridCursor}
          items={visible}
          onCreateTask={createTask}
          onMarkFiled={markFiled}
          onDelete={del}
        />
      ) : (
        <div className="card-flat overflow-x-auto" data-testid="compliance-timeline">
          <table className="tbl">
            <thead>
              <tr>
                <th><HeaderFilter label="Month" colKey="month" rows={items} cols={COLS} ctl={ctl} testid="hdr-month" /></th>
                <th><HeaderFilter label="Due" colKey="due" rows={items} cols={COLS} ctl={ctl} testid="hdr-due" /></th>
                <th><HeaderFilter label="Services" colKey="deadline" rows={items} cols={COLS} ctl={ctl} testid="hdr-deadline" /></th>
                <th><HeaderFilter label="Client" colKey="client" rows={items} cols={COLS} ctl={ctl} testid="hdr-client" /></th>
                <th><HeaderFilter label="Period" colKey="period" rows={items} cols={COLS} ctl={ctl} testid="hdr-period" /></th>
                <th><HeaderFilter label="Stage" colKey="stage" rows={items} cols={COLS} ctl={ctl} testid="hdr-stage" /></th>
                <th><HeaderFilter label="Status" colKey="status" rows={items} cols={COLS} ctl={ctl} testid="hdr-status" /></th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={8} className="text-center text-sm text-[var(--ink-2)] py-10" data-testid="compliance-no-match">No deadlines match these filters.</td></tr>
              )}
              {visible.map((d) => {
                const isYearEnd = d.deadline_type === "annual_year_end";
                return (
                <tr key={d.id} data-testid={`deadline-row-${d.id}`}>
                  <td className="text-xs whitespace-nowrap" data-testid={`month-${d.id}`}>{monthLabel(d)}</td>
                  <td className="font-mono-data text-xs whitespace-nowrap">{(d.due_date || "").slice(0, 10)}</td>
                  <td>
                    <div className="font-medium text-sm">{d.label}</div>
                    {d.notes && <div className="text-xs text-[var(--ink-2)] mt-0.5">{d.notes}</div>}
                  </td>
                  <td className="text-[var(--ink-2)]">{d.client_name || "—"}</td>
                  <td className="text-xs text-[var(--ink-2)]">{d.period || "—"}</td>
                  <td>
                    <select data-testid={`stage-${d.id}`} value={d.stage || ""} onChange={(e) => setStage(d.id, e.target.value)}
                      className={`input-slim text-xs !py-0.5 !w-40 ${d.stage === "Submitted" ? "text-[var(--ok)]" : d.stage === "Awaiting Approval" ? "text-[var(--warn)]" : ""}`}>
                      <option value="">—</option>
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td><span className={`status-pill ${STATUS_TONE[d.status]?.pill}`}>{STATUS_TONE[d.status]?.label || d.status}</span></td>
                  <td className="text-right">
                    <div className="flex gap-1.5 justify-end flex-wrap">
                      {!d.auto_task_id && d.status !== "filed" && (
                        <button data-testid={`convert-${d.id}`} onClick={() => createTask(d.id)} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" title="Create task">
                          <ArrowRight size={12} strokeWidth={1.5} /> Task
                        </button>
                      )}
                      {d.auto_task_id && <span className="text-[10px] text-[var(--ok)] font-mono-data self-center">TASK #{d.auto_task_id.slice(-6)}</span>}
                      {!isYearEnd && d.status !== "filed" && (
                        <button data-testid={`file-${d.id}`} onClick={() => markFiled(d.id)} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" title="Mark filed">
                          <CheckCircle2 size={12} strokeWidth={1.5} /> Filed
                        </button>
                      )}
                      <button data-testid={`del-${d.id}`} onClick={() => del(d.id)} className="btn-ghost !py-1 !px-2 text-xs text-[var(--err)]" title="Delete">
                        <Trash2 size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddDeadlineModal
          clients={clients}
          types={types}
          onClose={(saved) => { setShowAdd(false); if (saved) load(); }}
        />
      )}
      {showSuggest && (
        <AutoSuggestModal
          clients={clients}
          onClose={(count) => { setShowSuggest(false); if (count !== null) { setMsg(`${count} deadline${count === 1 ? "" : "s"} suggested.`); load(); } }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone, testid }) {
  const toneClass = tone === "err" ? "text-[var(--err)]" : tone === "ok" ? "text-[var(--ok)]" : tone === "warn" ? "text-[var(--warn)]" : "text-[var(--ink)]";
  return (
    <div className="card-flat px-4 py-2 flex items-center gap-3" data-testid={testid}>
      <span className="overline">{label}</span>
      <span className={`font-display text-2xl leading-none ${toneClass}`}>{value}</span>
    </div>
  );
}

function MonthGrid({ cursor, setCursor, items, onCreateTask, onMarkFiled, onDelete }) {
  const { year, month } = cursor;
  // First day of the month, and how many days
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // ISO weekday: Mon=1..Sun=7. We want Mon start.
  const startWeekday = ((first.getDay() + 6) % 7); // 0=Mon .. 6=Sun
  // Group items by day (YYYY-MM-DD in this month)
  const byDay = {};
  items.forEach((d) => {
    const dt = (d.due_date || "").slice(0, 10);
    if (!dt) return;
    const [dy, dm] = dt.split("-").map(Number);
    if (dy === year && dm === month + 1) {
      if (!byDay[dt]) byDay[dt] = [];
      byDay[dt].push(d);
    }
  });
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);
  const [selected, setSelected] = useState(null);
  const todayIso = new Date().toISOString().slice(0, 10);

  function pad(n) { return String(n).padStart(2, "0"); }
  function goto(delta) {
    let y = year, m = month + delta;
    if (m < 0) { m += 12; y--; }
    if (m > 11) { m -= 12; y++; }
    setCursor({ year: y, month: m });
  }

  return (
    <div className="card-flat overflow-hidden" data-testid="compliance-grid">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--line)]">
        <div className="flex items-center gap-3">
          <button data-testid="grid-prev" onClick={() => goto(-1)} className="btn-ghost p-1.5"><ChevronLeft size={14} strokeWidth={1.5} /></button>
          <h2 className="font-display text-2xl">{MONTHS_LONG[month]} <span className="text-[var(--ink-2)]">{year}</span></h2>
          <button data-testid="grid-next" onClick={() => goto(1)} className="btn-ghost p-1.5"><ChevronRight size={14} strokeWidth={1.5} /></button>
        </div>
        <button data-testid="grid-today" onClick={() => { const t = new Date(); setCursor({ year: t.getFullYear(), month: t.getMonth() }); }} className="btn-ghost text-xs">Today</button>
      </div>
      <div className="grid grid-cols-7 border-b border-[var(--line)] bg-[var(--bg)]">
        {WEEK_DAYS.map((d) => (
          <div key={d} className="px-3 py-2 text-[10px] font-mono-data uppercase tracking-widest text-[var(--ink-2)]">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, idx) => {
          if (day === null) return <div key={idx} className="min-h-[110px] border-r border-b border-[var(--line)] bg-[var(--bg)] opacity-40" />;
          const iso = `${year}-${pad(month + 1)}-${pad(day)}`;
          const dayItems = byDay[iso] || [];
          const isToday = iso === todayIso;
          return (
            <div
              key={idx}
              onClick={() => dayItems.length && setSelected({ iso, items: dayItems })}
              data-testid={`day-${iso}`}
              className={`min-h-[110px] p-2 border-r border-b border-[var(--line)] transition ${dayItems.length ? "cursor-pointer hover:bg-[var(--gold-soft)]" : ""} ${isToday ? "bg-[var(--navy-soft)]" : ""}`}
            >
              <div className={`text-[11px] font-mono-data mb-1 flex items-center justify-between ${isToday ? "text-[var(--navy)] font-semibold" : "text-[var(--ink-2)]"}`}>
                <span>{day}</span>
                {isToday && <span className="text-[9px] uppercase tracking-widest">Today</span>}
              </div>
              <div className="space-y-1">
                {dayItems.slice(0, 3).map((d) => (
                  <div key={d.id} data-testid={`day-item-${d.id}`} className={`text-[10px] leading-tight px-1.5 py-1 rounded border ${STATUS_TONE[d.status]?.pill}`}>
                    <div className="flex items-center gap-1">
                      <span className={`inline-block w-1 h-1 rounded-full ${STATUS_TONE[d.status]?.dot}`} />
                      <span className="font-medium truncate">{d.label}</span>
                    </div>
                    <div className="text-[9px] opacity-80 truncate">{d.client_name}</div>
                  </div>
                ))}
                {dayItems.length > 3 && (
                  <div className="text-[10px] text-[var(--ink-2)] font-mono-data">+{dayItems.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Day popover — simple bottom sheet */}
      {selected && (
        <>
          <div className="backdrop" onClick={() => setSelected(null)} />
          <div className="drawer" data-testid="day-drawer">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline mb-1">Statutory deadlines</div>
                  <h3 className="font-display text-2xl">{selected.iso}</h3>
                </div>
                <button onClick={() => setSelected(null)} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={16} strokeWidth={1.5} /></button>
              </div>
              <div className="space-y-2">
                {selected.items.map((d) => (
                  <div key={d.id} className="card-flat p-3" data-testid={`day-drawer-item-${d.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-medium text-sm">{d.label}</div>
                        <div className="text-xs text-[var(--ink-2)] mt-0.5">{d.client_name} · {d.period || "—"}</div>
                      </div>
                      <span className={`status-pill ${STATUS_TONE[d.status]?.pill}`}>{STATUS_TONE[d.status]?.label}</span>
                    </div>
                    {d.notes && <div className="text-xs text-[var(--ink-2)] mb-2">{d.notes}</div>}
                    <div className="flex gap-1.5 justify-end flex-wrap">
                      {!d.auto_task_id && d.status !== "filed" && (
                        <button onClick={() => { onCreateTask(d.id); setSelected(null); }} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1"><ArrowRight size={12} strokeWidth={1.5} /> Task</button>
                      )}
                      {d.auto_task_id && <span className="text-[10px] text-[var(--ok)] font-mono-data self-center">TASK #{d.auto_task_id.slice(-6)}</span>}
                      {d.deadline_type !== "annual_year_end" && d.status !== "filed" && (
                        <button onClick={() => { onMarkFiled(d.id); setSelected(null); }} className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1"><CheckCircle2 size={12} strokeWidth={1.5} /> Filed</button>
                      )}
                      <button onClick={() => { onDelete(d.id); setSelected(null); }} className="btn-ghost !py-1 !px-2 text-xs text-[var(--err)]"><Trash2 size={12} strokeWidth={1.5} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AddDeadlineModal({ clients, types, onClose }) {
  const [form, setForm] = useState({ client_id: "", deadline_type: "confirmation_statement", due_date: "", period: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.client_id || !form.due_date) {
      setError("Client and Due Date are required.");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.period) delete body.period;
      if (!body.notes) delete body.notes;
      await api.post("/compliance", body);
      onClose(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  }

  return (
    <>
      <div className="backdrop" onClick={() => onClose(false)} />
      <div className="drawer" data-testid="add-deadline-modal">
        <form onSubmit={submit} className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline mb-1">Statutory</div>
              <h2 className="font-display text-3xl">Add deadline.</h2>
            </div>
            <button type="button" onClick={() => onClose(false)} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={16} strokeWidth={1.5} /></button>
          </div>
          <div className="mb-4">
            <label className="label-overline">Client *</label>
            <select data-testid="ad-client" required className="input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
              <option value="">Select client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
            </select>
          </div>
          <div className="mb-4">
            <label className="label-overline">Deadline Type *</label>
            <select data-testid="ad-type" required className="input" value={form.deadline_type} onChange={(e) => setForm({ ...form, deadline_type: e.target.value })}>
              {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="label-overline">Due Date *</label>
              <input data-testid="ad-due" type="date" required className="input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div className="mb-4">
              <label className="label-overline">Period</label>
              <input data-testid="ad-period" placeholder="e.g. Q1 2026, YE 2025" className="input" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
            </div>
          </div>
          <div className="mb-4">
            <label className="label-overline">Notes</label>
            <textarea data-testid="ad-notes" rows={3} className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          {error && <div className="text-sm text-[var(--err)] mb-3" data-testid="ad-error">{error}</div>}
          <div className="flex gap-2 mt-6">
            <button data-testid="ad-submit" disabled={saving} className="btn-primary">{saving ? "Saving…" : "Add deadline"}</button>
            <button type="button" onClick={() => onClose(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
}

function AutoSuggestModal({ clients, onClose }) {
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function run() {
    if (!clientId) { setError("Select a client"); return; }
    setBusy(true); setError("");
    try {
      const r = await api.post(`/compliance/auto-suggest/${clientId}`);
      onClose(r.data.inserted);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  return (
    <>
      <div className="backdrop" onClick={() => onClose(null)} />
      <div className="drawer" data-testid="auto-suggest-modal">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline mb-1 flex items-center gap-2"><Sparkles size={12} strokeWidth={1.5} /> AI-assisted</div>
              <h2 className="font-display text-3xl">Auto-suggest deadlines.</h2>
              <div className="text-sm text-[var(--ink-2)] mt-2">Generate the next 12 months of statutory deadlines from the client&apos;s incorporation date, engagement date, and services (VAT, Payroll, Pension, Self-Assessment).</div>
            </div>
            <button onClick={() => onClose(null)} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={16} strokeWidth={1.5} /></button>
          </div>
          <div className="mb-4">
            <label className="label-overline">Client *</label>
            <select data-testid="as-client" className="input" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Select client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
            </select>
          </div>
          {error && <div className="text-sm text-[var(--err)] mb-3" data-testid="as-error">{error}</div>}
          <div className="flex gap-2 mt-6">
            <button data-testid="as-run" onClick={run} disabled={busy} className="btn-primary flex items-center gap-2"><Sparkles size={14} strokeWidth={1.5} /> {busy ? "Generating…" : "Generate suggestions"}</button>
            <button onClick={() => onClose(null)} className="btn-ghost">Cancel</button>
          </div>
          <div className="text-[11px] text-[var(--ink-2)] mt-4">Duplicates are skipped automatically. You can edit or delete any generated deadline.</div>
        </div>
      </div>
    </>
  );
}
